/**
 * The public, crawlable organization profile — types, derived copy and JSON-LD.
 *
 * This is the client mirror of the jsonb the three anonymous RPCs return
 * (migration 20260913120000_public_organization_profile_rpcs.sql):
 *
 *   get_public_organization_profile(p_slug)          → PublicOrganizationProfile | null
 *   get_public_organization_article(p_slug, p_id)    → PublicOrganizationArticle | null
 *   list_public_organizations()                      → PublicOrganizationIndexRow[]
 *
 * The SERVER decides what is public: every key here is one the migration's
 * closed allowlist emits, and nothing in this module reaches for a field the
 * RPC does not return. If a new field is wanted on the public page, it is
 * added to the migration's allowlist first and here second — never the other
 * way round, and never by reading `organizations` or `profile_data` directly.
 *
 * Dependency-free on purpose (no Supabase client, no React): the same code
 * runs in the browser, in the Node prerender bundle, and in unit tests.
 */

import type { PublicListingFactsPayload } from "@/lib/listingFacts";
import { SITE_ORIGIN, canonicalUrl } from "@/lib/seo";
import { ORGANIZATION_CONTENT_TYPE_LABELS, type OrganizationContentType } from "@/lib/organizationContentTypes";
import { ORG_TYPE_LABELS } from "@/lib/orgTypeOptions";
import { excerpt, readingTime, stripMarkdown } from "@/lib/markdownText";
import { stateName, type OfficesDisplay } from "@/lib/officesDisplay";
import { publicEffectiveOfficesDisplay, publicWorkModelView } from "@/lib/workModel";
import { parseOfficeString } from "@/lib/orgOffices";

export interface PublicOrgArticle {
  id: string;
  type: string;
  title: string;
  /** Markdown body. For third-party coverage this is the org's own summary, never the source body. */
  body: string;
  image_url: string | null;
  /** Original source when the item covers something published elsewhere. */
  link: string | null;
  featured: boolean;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface PublicOrgAward {
  id: string;
  name: string;
  org: string;
  year: number;
}

export interface PublicOrgOffice {
  city: string;
  state: string | null;
}

export interface PublicOrgStory {
  mission: string | null;
  our_story: string | null;
  what_makes_us_different: string | null;
  quote_text: string | null;
  quote_attribution: string | null;
}

export interface PublicOrgStat {
  label: string;
  value: string;
  context: string | null;
}

/** One onboarding stage as get_public_organization_profile projects it since migration 20260915120000. */
export interface PublicOrgOnboardingStage {
  milestone: string | null;
  title: string;
  description: string | null;
  goals: string[];
  activities: string[];
  sort_order: number | null;
}

/** One tool as the anonymous RPC emits it: exactly these seven keys, no logo, price or builder. */
export interface PublicTechTool {
  name: string;
  category_slug: string;
  in_house: boolean;
  driver: boolean;
  access: "included" | "discounted" | "agent_paid" | null;
  trained: boolean;
  note: string | null;
}

export interface PublicTechBucket {
  category: string;
  tools: PublicTechTool[];
}

export interface PublicOrgValue {
  compensation_models: string[];
  primary_compensation_model: string | null;
  fee_types: string[];
  no_recurring_fees: boolean | null;
  provides_leads: boolean | null;
  lead_sources: string[];
  lead_volume: string | null;
  training_frequency: string[];
  one_on_one_coaching: boolean | null;
  coaching_frequency: string | null;
  has_training_library: boolean | null;
  training_programs: string[];
  /** Objects after migration 20260915120000; a title-only string[] from an environment that has not applied it yet. */
  onboarding_stages: PublicOrgOnboardingStage[] | string[];
  career_stages: string[];
  has_transaction_coordinator: boolean | null;
  marketing_support: string[];
  selected_benefits: string[];
  has_health_insurance: boolean | null;
  has_retirement_plan: boolean | null;
  partnership_opportunities: boolean | null;
  partnership_types: string[];
  /** Only when the organization has a transaction coordinator. Absent before migration 20261016170000. */
  tc_details?: string | null;
  /** A detail string, or true for the legacy yes. Absent before migration 20261016170000. */
  admin_assistance?: string | true | null;
}

export interface PublicOrgRanking {
  rank: string | null;
  category: string;
  region: string | null;
  source: string | null;
}

export interface PublicOrgPerformance {
  reporting_year: string | null;
  production: {
    total_volume: string | null;
    total_transactions: string | null;
    avg_sale_price: string | null;
    volume_growth_yoy: string | null;
  };
  agents: {
    retention_rate: string | null;
    avg_tenure: string | null;
    new_agent_success_rate: string | null;
    income_goal_hit_rate: string | null;
  };
  rankings: PublicOrgRanking[];
  market_presence: {
    market_share: string | null;
    days_on_market: string | null;
    list_to_sale_ratio: string | null;
    specializations: string[];
  };
}

export interface PublicOrganizationProfile {
  id: string;
  slug: string;
  name: string;
  type: string | null;
  /**
   * "listed" for a basic listing (staff-listed, ownerless, identity values
   * only), "owned" otherwise (migration 20261022120000). Absent before it,
   * which means owned. Read through isListedPublicProfile (src/lib/listedOrganization.ts).
   */
  listing_kind?: "listed" | "owned" | null;
  /** Listing facts, sent for a basic listing only (absent on owned profiles). */
  phone?: string | null;
  street_address?: string | null;
  postal_code?: string | null;
  /**
   * Staff-approved public facts on a basic listing (migration
   * 20261023120000), in Compaire's words. Sent for a listing only (null when
   * none are approved); absent on owned profiles and before the migration.
   * Read through listingFactsFromPublic (src/lib/listingFacts.ts).
   */
  listing_facts?: PublicListingFactsPayload | null;
  tagline: string | null;
  description: string | null;
  logo_url: string | null;
  cover_url: string | null;
  city: string | null;
  state: string | null;
  website: string | null;
  founded_year: number | null;
  is_hiring: boolean;
  service_types: string[];
  office_locations: string[];
  offices: PublicOrgOffice[];
  /** "states" when the organization shows its footprint as states only. Absent before migration 20261016120000. */
  offices_display?: OfficesDisplay;
  /**
   * The cloud brokerage label, resolved on the server (migration 20261016150500):
   * "cloud" | "cloud_company_office" | "offices" | "unspecified". Read only through
   * publicWorkModelView (src/lib/workModel.ts), which treats any other value,
   * or its absence before that migration, as unspecified.
   */
  work_model?: string | null;
  published_at: string | null;
  updated_at: string;
  story: PublicOrgStory;
  by_the_numbers: PublicOrgStat[];
  agent_count: number | null;
  /**
   * Tool display names, string[] (migration 20261016230000): the shape every
   * deployed bundle can render. Only migration 20261016170000 alone emitted
   * the grouping here, so an environment between those two migrations can
   * still send PublicTechBucket[]. Read through fromPublicProfile, never directly.
   */
  technology_tools: Array<string | PublicTechBucket>;
  /** The grouped tools (migration 20261016230000). Absent before it. */
  technology_tool_groups?: PublicTechBucket[];
  brand_video_url: string | null;
  gallery_photos: string[];
  value: PublicOrgValue;
  performance: PublicOrgPerformance;
  content: PublicOrgArticle[];
  awards: PublicOrgAward[];
  /**
   * "Who we're looking for" (migration 20260918200000). Absent on an
   * environment that has not applied it, and NULL when the org never saved it.
   */
  looking_for?: PublicOrgLookingFor | null;
}

/** The public projection of profile_data.lookingFor. Named keys only. */
export interface PublicOrgLookingFor {
  headline: string | null;
  markets: string[];
  open_to_all: boolean;
  requirements: {
    min_years_experience: number | null;
    min_volume_range: number | null;
    min_transaction_range: number | null;
    market_focus: string[];
    specialties: string[];
  };
  announcement: { text: string; expires_at: string } | null;
  updated_at: string | null;
}

/**
 * The public projection back into the editor's shape, so the public page
 * renders through the same normalizeLookingFor and LookingForCard as the
 * signed-in page. Missing or null means the org has said nothing.
 */
export function lookingForFromPublic(p: PublicOrgLookingFor | null | undefined): Record<string, unknown> | null {
  if (!p || typeof p !== "object") return null;
  const r = p.requirements ?? ({} as PublicOrgLookingFor["requirements"]);
  return {
    headline: p.headline ?? undefined,
    markets: p.markets ?? [],
    openToAll: p.open_to_all === true,
    requirements: {
      minYearsExperience: r.min_years_experience ?? undefined,
      minVolumeRange: r.min_volume_range ?? undefined,
      minTransactionRange: r.min_transaction_range ?? undefined,
      marketFocus: r.market_focus ?? [],
      specialties: r.specialties ?? [],
    },
    announcement: p.announcement ? { text: p.announcement.text, expiresAt: p.announcement.expires_at } : null,
    updatedAt: p.updated_at ?? undefined,
  };
}

export interface PublicOrganizationArticle {
  organization: {
    id: string;
    slug: string;
    name: string;
    type: string | null;
    logo_url: string | null;
    city: string | null;
    state: string | null;
    website: string | null;
  };
  article: PublicOrgArticle;
}

export interface PublicOrganizationIndexRow {
  slug: string;
  updated_at: string;
  articles: { id: string; published_at: string | null; updated_at: string }[];
}

/**
 * The public cloud badge ("Cloud brokerage" / "Part of a cloud brokerage") for
 * a public profile's closed `work_model` value, or null. Used by the brokerage
 * directory (#1050), so the label rule stays with the listed readers.
 */
export function publicCloudBadge(workModel: unknown): string | null {
  return publicWorkModelView(workModel).badge;
}

// ---------------------------------------------------------------- paths

export function organizationPath(slug: string): string {
  return `/organization/${encodeURIComponent(slug)}`;
}

export function organizationArticlePath(slug: string, contentId: string): string {
  return `${organizationPath(slug)}/news/${encodeURIComponent(contentId)}`;
}

// ---------------------------------------------------------------- derived copy

const NON_EMPTY = (v: string | null | undefined): v is string => typeof v === "string" && v.trim() !== "";

/**
 * An admin-typed URL that is safe to place in an anchor `href` or in JSON-LD
 * on a page served to anonymous visitors: absolute, http or https, nothing
 * else. `organizations.website` and `organization_content.link` are stored
 * as typed, with no scheme check on write, so a `javascript:` or `data:`
 * value must be dropped here rather than rendered. Returns null for anything
 * that is not such a URL.
 */
export function httpUrl(value: string | null | undefined): string | null {
  if (!NON_EMPTY(value)) return null;
  const trimmed = value.trim();
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return null;
  }
  // The original spelling is kept: URL.href would add a trailing slash to a
  // bare host and rewrite what the admin typed.
  return parsed.protocol === "http:" || parsed.protocol === "https:" ? trimmed : null;
}

export function orgTypeLabel(type: string | null | undefined): string | null {
  if (!type) return null;
  return (ORG_TYPE_LABELS as Record<string, string>)[type] ?? type.charAt(0).toUpperCase() + type.slice(1);
}

export function contentTypeLabel(type: string): string {
  return (
    ORGANIZATION_CONTENT_TYPE_LABELS[type as OrganizationContentType] || (type ? type.replace(/_/g, " ") : "Update")
  );
}

/** "El Dorado Hills, CA" from the row's own columns, or null when neither is set. */
export function locationLabel(o: { city: string | null; state: string | null }): string | null {
  const city = NON_EMPTY(o.city) ? o.city.trim() : null;
  const state = NON_EMPTY(o.state) ? o.state.trim() : null;
  if (city && state) return `${city}, ${state}`;
  return city ?? state;
}

/**
 * Title for the tab, og:title and the headline crawlers quote. Kept as a name:
 * "Ardenwell Group — Real Estate Team in El Dorado Hills, CA | Compaire".
 */
export function profileTitle(p: PublicOrganizationProfile): string {
  const kind = orgTypeLabel(p.type);
  const where = locationLabel(p);
  const descriptor = [kind ? `Real Estate ${kind}` : null, where ? `in ${where}` : null].filter(Boolean).join(" ");
  return descriptor ? `${p.name} — ${descriptor} | Compaire` : `${p.name} | Compaire`;
}

/** Meta description: the tagline, else the mission, else the description — plain text, ≤ 160 chars. */
export function profileDescription(p: PublicOrganizationProfile): string {
  const source = [p.tagline, p.story.mission, p.description, p.story.our_story].find(NON_EMPTY);
  if (source) return excerpt(source, 160);
  const where = locationLabel(p);
  const kind = orgTypeLabel(p.type)?.toLowerCase() ?? "organization";
  return `${p.name} is a real estate ${kind}${where ? ` in ${where}` : ""}. Explore its profile on Compaire.`;
}

export function articleTitle(a: PublicOrganizationArticle): string {
  return `${a.article.title} — ${a.organization.name} | Compaire`;
}

export function articleDescription(a: PublicOrganizationArticle): string {
  return excerpt(a.article.body, 160) || `${contentTypeLabel(a.article.type)} from ${a.organization.name} on Compaire.`;
}

export function articleExcerpt(a: PublicOrgArticle): string {
  return excerpt(a.body);
}

export function articleReadTime(a: PublicOrgArticle): string | null {
  return readingTime(a.body);
}

export function articleDate(a: PublicOrgArticle): string | null {
  return a.published_at ?? a.created_at ?? null;
}

/** "September 10, 2026" from an ISO timestamp; the raw value if unparsable. */
export function formatLongDate(value: string | null | undefined): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" });
}

/** YYYY-MM-DD for sitemap lastmod and JSON-LD dates; null when unparsable. */
export function isoDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
}

/**
 * Compact currency for the self-reported production figures, which the editor
 * stores as digit strings ("228000000" → "$228M"). Anything that is not a
 * plain number is returned as typed so a value like "N/A" is not mangled.
 */
export function formatCompactCurrency(value: string | null | undefined): string | null {
  if (!NON_EMPTY(value)) return null;
  const raw = value.replace(/[$,\s]/g, "");
  if (!/^\d+(\.\d+)?$/.test(raw)) return value;
  const n = Number(raw);
  if (n >= 1_000_000_000) return `$${trimZeros((n / 1_000_000_000).toFixed(1))}B`;
  if (n >= 1_000_000) return `$${trimZeros((n / 1_000_000).toFixed(1))}M`;
  if (n >= 1_000) return `$${trimZeros((n / 1_000).toFixed(0))}K`;
  return `$${n.toLocaleString("en-US")}`;
}

function trimZeros(s: string): string {
  return s.replace(/\.0$/, "");
}

/** Percent from a stored "86" / "86%" / "0.86"-style string. */
export function formatPercent(value: string | null | undefined): string | null {
  if (!NON_EMPTY(value)) return null;
  const raw = value.replace(/[%\s]/g, "");
  if (!/^\d+(\.\d+)?$/.test(raw)) return value;
  return `${trimZeros(Number(raw).toFixed(1))}%`;
}

export function formatInteger(value: string | null | undefined): string | null {
  if (!NON_EMPTY(value)) return null;
  const raw = value.replace(/[,\s]/g, "");
  if (!/^\d+$/.test(raw)) return value;
  return Number(raw).toLocaleString("en-US");
}

// ---------------------------------------------------------------- JSON-LD

const WEBSITE_ID = `${SITE_ORIGIN}/#website`;

function compact<T extends Record<string, unknown>>(obj: T): T {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === null || v === undefined) continue;
    if (Array.isArray(v) && v.length === 0) continue;
    out[k] = v;
  }
  return out as T;
}

function organizationNode(o: {
  slug: string;
  name: string;
  logo_url: string | null;
  website: string | null;
  city: string | null;
  state: string | null;
}) {
  const url = canonicalUrl(organizationPath(o.slug));
  return compact({
    "@type": "RealEstateAgent",
    "@id": `${url}#organization`,
    name: o.name,
    url,
    logo: o.logo_url ?? undefined,
    sameAs: httpUrl(o.website) ? [httpUrl(o.website)] : undefined,
    address:
      o.city || o.state
        ? compact({
            "@type": "PostalAddress",
            addressLocality: o.city ?? undefined,
            addressRegion: o.state ?? undefined,
            addressCountry: "US",
          })
        : undefined,
  });
}

/**
 * areaServed for the profile's JSON-LD: each office's "City, ST", or — when
 * the organization shows its footprint as states only, by its own choice or
 * because it is a cloud brokerage — one schema.org State per state (a
 * stateless office keeps its city label).
 */
export function organizationAreaServed(p: PublicOrganizationProfile): Array<string | Record<string, string>> {
  if (publicEffectiveOfficesDisplay(p.offices_display, p.work_model) !== "states") {
    return p.offices.length
      ? p.offices.map((o) => locationLabel(o)).filter(NON_EMPTY)
      : p.office_locations.filter(NON_EMPTY);
  }
  // Read the state from the structured office; only the legacy mirror is parsed.
  const offices = p.offices.length ? p.offices : p.office_locations.filter(NON_EMPTY).map(parseOfficeString);
  const out: Array<string | Record<string, string>> = [];
  const seen = new Set<string>();
  for (const o of offices) {
    const name = stateName(o.state);
    const key = name || locationLabel(o);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(name ? { "@type": "State", name } : key);
  }
  return out;
}

/**
 * Structured data for the profile page: the organization as a
 * schema.org RealEstateAgent (the LocalBusiness subtype search engines and
 * answer engines use for brokerages and teams), the page, and a breadcrumb.
 * Only allowlisted, already-public values are ever placed here.
 */
export function organizationJsonLd(p: PublicOrganizationProfile): Record<string, unknown> {
  const url = canonicalUrl(organizationPath(p.slug));
  const org = organizationNode(p);
  const description = profileDescription(p);
  const areaServed = organizationAreaServed(p);

  return {
    "@context": "https://schema.org",
    "@graph": [
      compact({
        ...org,
        description,
        image: p.cover_url ?? p.logo_url ?? undefined,
        foundingDate: p.founded_year ? String(p.founded_year) : undefined,
        slogan: p.tagline ?? undefined,
        numberOfEmployees: p.agent_count ? { "@type": "QuantitativeValue", value: p.agent_count } : undefined,
        areaServed: areaServed.length ? areaServed : undefined,
        knowsAbout: p.service_types.length ? p.service_types : undefined,
        award: p.awards.length ? p.awards.map((a) => `${a.name} (${a.org}, ${a.year})`) : undefined,
      }),
      {
        "@type": "WebPage",
        "@id": url,
        url,
        name: profileTitle(p),
        description,
        isPartOf: { "@id": WEBSITE_ID },
        about: { "@id": org["@id"] },
        dateModified: isoDate(p.updated_at) ?? undefined,
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Teams & Brokerages", item: canonicalUrl("/teams-brokerages") },
          { "@type": "ListItem", position: 2, name: p.name, item: url },
        ],
      },
    ],
  };
}

/**
 * NewsArticle for one newsroom item. An item that covers something published
 * elsewhere carries the original as `isBasedOn`, so the citation points at
 * the source and Compaire is not claiming the third party's body.
 */
export function articleJsonLd(a: PublicOrganizationArticle): Record<string, unknown> {
  const org = organizationNode(a.organization);
  const url = canonicalUrl(organizationArticlePath(a.organization.slug, a.article.id));
  const published = isoDate(articleDate(a.article));
  const modified = isoDate(a.article.updated_at) ?? published;
  const wordCount = stripMarkdown(a.article.body).split(/\s+/).filter(Boolean).length;

  return {
    "@context": "https://schema.org",
    "@graph": [
      org,
      compact({
        "@type": "NewsArticle",
        "@id": url,
        mainEntityOfPage: url,
        url,
        headline: a.article.title,
        description: articleDescription(a),
        image: a.article.image_url ?? undefined,
        datePublished: published ?? undefined,
        dateModified: modified ?? undefined,
        wordCount: wordCount || undefined,
        articleSection: contentTypeLabel(a.article.type),
        isBasedOn: httpUrl(a.article.link) ?? undefined,
        author: { "@id": org["@id"] },
        publisher: { "@id": org["@id"] },
        about: { "@id": org["@id"] },
        isPartOf: { "@id": WEBSITE_ID },
      }),
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Teams & Brokerages", item: canonicalUrl("/teams-brokerages") },
          {
            "@type": "ListItem",
            position: 2,
            name: a.organization.name,
            item: canonicalUrl(organizationPath(a.organization.slug)),
          },
          { "@type": "ListItem", position: 3, name: a.article.title, item: url },
        ],
      },
    ],
  };
}

/**
 * Serialize for a <script type="application/ld+json"> body. `<` is escaped so
 * an admin-typed "</script>" inside a title or body can never close the tag.
 */
export function serializeJsonLd(data: Record<string, unknown>): string {
  return JSON.stringify(data).replace(/</g, "\\u003c");
}
